import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {getDefaultStore} from "jotai";
import {PostHog} from "posthog-js";
import {PostHogProvider} from "posthog-js/react";
import type {FC, PropsWithChildren} from "react";

export const capturePostHogEvent = (eventName: PostHogEvents, data?: Record<string, unknown>): void => {
  try {
    const user = useUserStore.getState().user;

    posthog.capture(eventName, {
      companyId: getDefaultStore().get(selectedCompanyIdAtom),
      packageName: user.package_subscription?.name ?? "NONE",
      source: "frontend",
      userId: user.id,
      userType: user.user_access_level,
      ...data,
    });
  } catch (error) {
    console.error(`Failed to capture PostHog event "${eventName}":`, error);
  }
};

export const posthog = new PostHog();

const isCookielessPostHog = window.location.pathname.includes("/embed/");

if (import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true") {
  posthog.init(import.meta.env.VITE_POSTHOG_PUBLIC_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_PUBLIC_HOST,
    cookieless_mode: isCookielessPostHog ? "always" : "on_reject",
    defaults: "2026-05-30",
    opt_out_capturing_by_default: !isCookielessPostHog,
  });

  posthog.set_config({
    disable_session_recording:
      isCookielessPostHog ||
      (typeof process.env.ENV_IS_PRODUCTION === "undefined"
        ? import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "false"
        : process.env.ENV_IS_PRODUCTION === "false"),
    enable_heatmaps: !isCookielessPostHog,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {password: true},
    },
    capture_heatmaps: !isCookielessPostHog,
    capture_dead_clicks: !isCookielessPostHog,
    capture_performance: !isCookielessPostHog,
    autocapture: false,
    capture_exceptions: false,
  });
}

export enum PostHogEvents {
  AdCodeCopied = "ad_code_copied",
  AiCampaignCreationCompleted = "ai_campaign_creation_completed",
  AiCampaignHintApplied = "ai_campaign_hint_applied",
  AiCampaignHintSkipped = "ai_campaign_hint_skipped",
  AiCampaignVoiceInputUsed = "ai_campaign_voice_input_used",
  AmbassadorshipRequestSent = "ambassadorship_request_sent",
  ApplicationCancelled = "application_cancelled",
  ApplicationRefunded = "application_refunded",
  ApplicationsPageViewed = "applications_page_viewed",
  BundlePurchased = "bundle_purchased",
  CampaignCompleted = "campaign_completed",
  CampaignCreationCancelled = "campaign_creation_cancelled",
  CampaignPaused = "campaign_paused",
  CampaignResumed = "campaign_resumed",
  ChatBulkMessageSent = "chat_bulk_message_sent",
  ChatMessageSent = "chat_message_sent",
  CompanyProfileUpdated = "company_profile_updated",
  CompanyRegistrationFinished = "company_registration_finished",
  CompanyRegistrationFinishedPaymentLink = "company_registration_finished_payment_link",
  ContentApproved = "content_approved",
  ContentRejected = "content_rejected",
  ContentRevisionRequested = "content_revision_requested",
  ContinueDraftCampaignCreation = "continue_draft_campaign_creation",
  CreditsModified = "credits_modified",
  DraftCampaignDeleted = "draft_campaign_deleted",
  DuplicateAsInstagramDraft = "duplicate_as_instagram_draft",
  DuplicateAsTiktokDraft = "duplicate_as_tiktok_draft",
  DuplicateAsUGCDraft = "duplicate_as_ugc_draft",
  ExclusivityFilterUpgradeClicked = "exclusivity_filter_upgrade_clicked",
  InfluencerAccepted = "influencer_accepted",
  InfluencerAddedToGroup = "influencer_added_to_group",
  InfluencerApplied = "influencer_applied",
  InfluencerCardClicked = "influencer_card_clicked",
  InfluencerDeclined = "influencer_declined",
  InfluencerGroupSelectorOpen = "influencer_group_selector_open",
  InfluencerProfileViewed = "influencer_profile_viewed",
  InfluencerRemovedFromGroup = "influencer_removed_from_group",
  InfluencerSearchPerformed = "influencer_search_performed",
  InvitedUserRegistrationFinished = "invited_user_registration_finished",
  InvoiceDownloaded = "invoice_downloaded",
  JoinLinkCopied = "join_link_copied",
  JoinLinkSharePromptClicked = "join_link_share_prompt_clicked",
  JoinLinkSharePromptDismissed = "join_link_share_prompt_dismissed",
  JoinPageBrandCtaClicked = "join_page_brand_cta_clicked",
  JoinPageDownloadAppClicked = "join_page_download_app_clicked",
  JoinPageEmailHandleSubmitted = "join_page_email_handle_submitted",
  JoinPageOpened = "join_page_opened",
  JoinPageReferralCodeCopied = "join_page_referral_code_copied",
  NotificationPreferenceChanged = "notification_preference_changed",
  OnboardingSignupCompleted = "onboarding_signup_completed",
  OnboardingStarted = "onboarding_started",
  OnboardingUrlBlocked = "onboarding_url_blocked",
  OnboardingWebsiteCrawlCompleted = "onboarding_website_crawl_completed",
  OnboardingWebsiteCrawlFailed = "onboarding_website_crawl_failed",
  PackageSubscriptionCreated = "package_subscription_created",
  PackageSubscriptionUpgraded = "package_subscription_upgraded",
  PageViewed = "page_viewed",
  PaymentFailedModalViewed = "payment_failed_modal_viewed",
  PaymentRetryAttempted = "payment_retry_attempted",
  PendingAmbassadorAccepted = "pending_ambassador_accepted",
  PendingAmbassadorDeclined = "pending_ambassador_declined",
  PendingAmbassadorsBulkAccepted = "pending_ambassadors_bulk_accepted",
  PendingAmbassadorsBulkDeclined = "pending_ambassadors_bulk_declined",
  PublishCampaign = "publish_campaign",
  PublishedCampaignDeleted = "published_campaign_deleted",
  RefluencedAppOpened = "refluenced_app_opened",
  RegistrationStepCompleted = "registration_step_completed",
  ReportingTabViewed = "reporting_tab_viewed",
  RevertInfluencerDecisionBackToPending = "revert_influencer_decision_back_to_pending",
  SaveAsDraft = "save_as_draft",
  StartCampaign = "start_campaign",
  StartCampaignCreation = "start_campaign_creation",
  StartCampaignCreationAI = "start_campaign_creation_ai",
  StepThreeCampaignCreation = "step_three_campaign_creation",
  StepTwoCampaignCreation = "step_two_campaign_creation",
  SubscriptionCancelled = "subscription_cancelled",
  SurveyAfterCampaignCreationAUG2025 = "survey_after_campaign_creation_AUG2025",
  SurveyAfterReportingAUG2025 = "survey_after_reporting_AUG2025",
  ThemeChanged = "theme_changed",
  TiktokVisibilityCreateCampaignClicked = "tiktok_visibility_create_campaign_clicked",
  TiktokVisibilityKeywordsDeleted = "tiktok_visibility_keywords_deleted",
  TiktokVisibilityProductLinesUpdated = "tiktok_visibility_product_lines_updated",
  TiktokVisibilityScanCompleted = "tiktok_visibility_scan_completed",
  TiktokVisibilityScanFailed = "tiktok_visibility_scan_failed",
  TiktokVisibilityScanStarted = "tiktok_visibility_scan_started",
  UserInvitedToAgency = "user_invited_to_agency",
  UserInvitedToCompany = "user_invited_to_company",
  UserRoleChanged = "user_role_changed",
}

export enum PostHogGroups {
  Agency = "agency",
  Company = "company",
  Subbrand = "subbrand",
}

if (
  typeof process.env.ENV_IS_PRODUCTION === "undefined"
    ? import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "false"
    : process.env.ENV_IS_PRODUCTION === "false"
) {
  posthog.debug(false);
}

const SharedPostHogProvider: FC<PropsWithChildren & {enabled: boolean}> = ({children, enabled}) => {
  if (!enabled) {
    return <>{children}</>;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};

export default SharedPostHogProvider;
